import type { SxProps, Theme } from '@mui/material';
import type { DocumentNode } from 'graphql';
import type { FindPalletTags, FindPalletTagsWithLocation } from 'types/graphql';

import { FIND_PALLET_TAGS, FIND_PALLET_TAGS_WITH_LOCATION } from '../api/pallet-tag-picker.queries';

/** What `query` returns; FindPalletTags is the same selection without `warehouseLocation`. */
export type PalletTagPickerData = FindPalletTags | FindPalletTagsWithLocation;

/**
 *   variant         query                        fetch                    read-only   input / footer / save
 *   productionRun   FindPalletTags               on lot change            all tags    unit-rounded qty + weight,
 *                                                                                     "N units selected", raw save
 *   transfer        FindPalletTagsWithLocation   once per lot; reopen     selected    2dp qty + location,
 *                                                retries an empty list                "X out of Y", rounded save
 *
 * With a pick target (`unitsToCompare`, the transfer row's units) a tick takes only the units the row
 * still needs, and Save is disabled while the saved quantities would exceed it.
 *
 * `fetchMode` carries the read-only column: `onLotChangeOrWhenEmpty` lists only the selected tags when read-only.
 */
export interface PalletTagPickerVariant {
  readonly query: DocumentNode;
  readonly fetchMode: 'onLotChange' | 'onLotChangeOrWhenEmpty';
  readonly locationColumn: boolean;
  readonly weight: { readonly tracked: boolean; readonly syncInputCheckbox: boolean };
  readonly quantityInput: 'inventoryQuantity' | 'twoDecimals';
  readonly footer: 'unitsSelected' | 'unitsPickedOfTarget';
  readonly roundQuantityOnSave: boolean;
  readonly dismissLabel: { readonly editable: string; readonly readOnly: string };
  readonly promptLayout: 'flexRow' | 'inline';
  readonly lotLinkSx?: SxProps<Theme>;
}

export const productionRunPalletTagPicker: PalletTagPickerVariant = {
  query: FIND_PALLET_TAGS,
  fetchMode: 'onLotChange',
  locationColumn: false,
  weight: { tracked: true, syncInputCheckbox: true },
  quantityInput: 'inventoryQuantity',
  footer: 'unitsSelected',
  roundQuantityOnSave: false,
  dismissLabel: { editable: 'Cancel', readOnly: 'Close' },
  promptLayout: 'flexRow',
  lotLinkSx: { fontSize: '16px' },
};

export const transferPalletTagPicker: PalletTagPickerVariant = {
  query: FIND_PALLET_TAGS_WITH_LOCATION,
  fetchMode: 'onLotChangeOrWhenEmpty',
  locationColumn: true,
  weight: { tracked: false, syncInputCheckbox: false },
  quantityInput: 'twoDecimals',
  footer: 'unitsPickedOfTarget',
  roundQuantityOnSave: true,
  dismissLabel: { editable: 'Cancel', readOnly: 'Cancel' },
  promptLayout: 'inline',
};
